<template>
  <div class="sb_add-text">
    <Wrapper>
      <Title>Eigen tekst toevoegen</Title>
      <Subtitle>Voeg een persoonlijke tekst / boodschap toe.</Subtitle>
      <div class="sb_add-text_textarea">
        <Label class="sb_add-text_label">
          <template #title>Jouw text</template>
          <template #input>
            <textarea ref="textarea" v-model="content" />
          </template>
        </Label>
      </div>
    </Wrapper>
    <hr />
    <Wrapper class="sb_add-text_font">
      <Label class="sb_add-text_label" :disabled="loading || !content">
        <template #title>Selecteer een lettertype</template>
        <template #input>
          <select v-model="font">
            <option
              v-for="(font, i) in fonts"
              :key="`sb-add-font-select-${i}`"
              :value="font.name"
              :style="{ 'font-family': font.name }"
              >{{ font.name }}</option
            >
          </select>
          <div v-if="loading" class="sb_add-text_label_spinner">
            <Spinner />
          </div>
        </template>
      </Label>
    </Wrapper>
    <hr />
    <Wrapper class="sb_add-text_color">
      <ColorPicker :width="270" :height="270" class="sb_add-text_color_picker" v-model="colour" :disabled="!content" />
      <Label class="sb_add-text_label" :disabled="!content">
        <template #title>Kleur</template>
        <template #input>
          <input v-model="colour" @input="validateHex" />
        </template>
      </Label>
      <span v-if="hexError">{{ hexError }}</span>
    </Wrapper>
  </div>
</template>

<script>
import ColorPicker from 'vue-color-picker-wheel';
import FontFaceObserver from 'fontfaceobserver';
import { mapState, mapMutations } from 'vuex';

export default {
  components: {
    ColorPicker,
  },
  data() {
    return {
      content: null,
      colour: '#000000',
      font: null,
      hexError: null,
      textObject: null,
      loadedFonts: [],
      loading: null,
    };
  },
  props: {
    active: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState('canvas', ['manager']),
    ...mapState('fonts', ['fonts']),
  },
  watch: {
    async colour() {
      await this.setText();
    },
    async content() {
      await this.setText();
    },
    async font(newVal, oldVal) {
      await this.setText(oldVal);
    },
  },
  activated() {
    if (this.textObject) {
      this.manager.selectObject(this.textObject);
    }
  },
  mounted() {
    this.font = this.fonts[0].name;
    this.loadFont(this.font);
    window.addEventListener('object:removed', this.checkRemove);
  },
  beforeDestroy() {
    window.removeEventListener('object:removed', this.checkRemove);
  },
  methods: {
    ...mapMutations('canvas', ['removeObject', 'setDesign']),
    ...mapMutations('modal', ['setModalData']),
    checkRemove({ detail }) {
      if (detail === this.textObject) {
        this.textObject = null;
        this.content = null;
      }
    },
    validateHex() {
      this.hexError = null;
      const regex = new RegExp(/^#[A-Fa-f0-9]{3}([A-Fa-f0-9]{3})?$/);
      const valid = regex.test(this.colour);
      if (!valid) {
        this.hexError = 'Deze hex is geen geldige hex.';
        return false;
      }

      return true;
    },
    validateContent() {
      if (!this.content) {
        this.removeObject(this.textObject);
        return false;
      }

      return true;
    },
    validate() {
      if (!this.validateContent()) {
        return false;
      }
      if (!this.validateHex()) {
        return false;
      }

      return true;
    },
    async loadFont(font) {
      const observer = new FontFaceObserver(font);
      this.loading = true;
      try {
        await observer.load();
      } catch (e) {
        this.setModalData({
          title: 'Woeps!',
          content: 'Something went wrong loading this font, please try again.',
        });
        this.loading = false;
        return false;
      }
      this.loading = false;
      this.loadedFonts.push(font);
      return true;
    },
    async setText(oldVal) {
      const { font, content, colour } = this;

      if (!this.validate()) {
        return;
      }

      if (!this.loadedFonts.includes(font)) {
        const loaded = await this.loadFont(font);
        if (!loaded) {
          this.font = oldVal;
          return;
        }
      }

      if (this.textObject) {
        this.manager.setText(this.textObject, {
          text: content,
          fontFamily: font,
          fill: colour,
        });

        this.setDesign();

        return;
      }

      this.textObject = this.manager.addText({
        text: content,
        fill: colour,
        fontFamily: font,
      });

      this.setDesign();

      this.manager.selectObject(this.textObject);
    },
  },
};
</script>

<style lang="scss">
#sb_fotowand-designer {
  .sb_add-text {
    position: relative;
    user-select: none;
    &_textarea {
      margin-top: 3rem;
    }
    &_font {
      width: 100%;
    }
    &_label {
      width: 100% !important;
      &_spinner {
        position: absolute;
        right: 1rem;
        font-size: 1.25rem;
        top: 50%;
        color: $brand-primary;
        transform: translateY(-50%);
      }
    }
    &_color {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      &_picker {
        transform: scale(0.85) translateY(-1rem);
        margin-bottom: 0rem;
        @include media('>=medium') {
          margin-bottom: 1.6rem;
        }
      }
    }
  }
}
</style>
